import { useMemo } from "react"
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
  useSettingsForm,
} from "~/components/design-system/pages"
import { Slider } from "~/components/reusable/Slider"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useCompany, useUpdateForecastAlertThreshold } from "~/hooks/useCompany"
import { api } from "~/utils/api"
import { formatName } from "~/utils/namingUtils"

export const DEFAULT_FORECAST_ALERT_THRESHOLD = 0.1
export const MIN_FORECAST_ALERT_THRESHOLD = 0.01
export const MAX_FORECAST_ALERT_THRESHOLD = 1

export function ForecastAlertThresholdCard() {
  const { data: companyData } = useCompany()
  const updateSettings = useUpdateForecastAlertThreshold()
  const utils = api.useUtils()
  const saved = useMemo(
    () => ({
      forecastAlertThreshold:
        companyData?.forecastAlertThreshold ?? DEFAULT_FORECAST_ALERT_THRESHOLD,
    }),
    [companyData?.forecastAlertThreshold],
  )
  const { values, setField, reset, submit, isSubmitting, isDirty } = useSettingsForm(saved)
  const threshold = values.forecastAlertThreshold

  return (
    <SettingsPage
      title="Alerts"
      form={{
        isDirty,
        isSaving: isSubmitting,
        requires: "staff",
        onSave: () =>
          submit(async (current) => {
            await updateSettings.mutateAsync(current)
            await utils.company.getCompany.invalidate()
          }),
        onDiscard: reset,
      }}
    >
      <SettingsSection title="Demand alerts">
        <SettingsRow
          label="Alert threshold"
          htmlFor="forecast-alert-threshold"
          description={
            <Tooltip
              body={`Controls the sensitivity of demand alerts in the product table. A lower value means more alerts will be triggered. The threshold is applied to unexplained growth and pre-order changes relative to ${formatName("sale", true)} over the last 52 weeks.`}
            >
              <span>
                Set the minimum percentage change (relative to {formatName("sale", true)} last 52
                weeks) that triggers a demand alert.
              </span>
            </Tooltip>
          }
        >
          <Slider
            requires="staff"
            wrapperClassName="w-[200px]"
            id="forecast-alert-threshold"
            value={[threshold]}
            onValueChange={(value) =>
              setField("forecastAlertThreshold", value[0] ?? DEFAULT_FORECAST_ALERT_THRESHOLD)
            }
            min={MIN_FORECAST_ALERT_THRESHOLD}
            max={MAX_FORECAST_ALERT_THRESHOLD}
            step={0.01}
          />
          <span className="w-10 text-right paragraph-sm text-fg-subtle tabular-nums">
            {Math.round(threshold * 100)}%
          </span>
        </SettingsRow>
      </SettingsSection>
    </SettingsPage>
  )
}
